import { Injectable } from '@angular/core';
import { EndUserViewDto, GenericViewDisplayConfig } from '@osos/shared/api';
import {
  DataViewsPresenterModel,
  DataViewStyle,
  DefaultSidebarEnum,
  EntryActionOperationEnum,
  PageContainerLayoutMode,
  ShowWorkflowConfig,
  ViewDisplayOptions,
} from '@osos/shared/types';
import { ParserBase } from '../../bases/parser/parser-base';
import { FieldFactoryService } from '../../factories';
type PageContainerLayoutType = { mode: PageContainerLayoutMode };

@Injectable()
export class EndUserDataViewsParserV1Service extends ParserBase<
  EndUserViewDto,
  DataViewsPresenterModel
> {
  constructor(private fieldFactory: FieldFactoryService) {
    super(EndUserViewDto, DataViewsPresenterModel, 1);
  }

  getPresenterModel(model: EndUserViewDto): DataViewsPresenterModel {
    const presenter = {
      specificationVersion: this.version,
      dataEntity: model.dataEntity,
      dataSource: model.dataSource,
      description: model.description,
      domain: model.domain,
      implicitType: model.implicitType,
      screen: model.screen,
      filterFirstConfigDto: model.filterFirstConfigDto
        ? {
            ...model.filterFirstConfigDto,
            fields: model.filterFirstConfigDto.fields.map((x) => ({
              ...x,
              fieldDefinition: x.fieldDefinition
                ? this.fieldFactory.createPresenterModel(x.fieldDefinition)
                : undefined,
            })),
          }
        : undefined,
      editConfiguration: {
        enabled: model.editConfiguration?.enabled,
        adminActions: model.editConfiguration?.adminActions,
        allowCreateEntryAsDraft:
          model.editConfiguration?.allowCreateEntryAsDraft,
        allowQuickCreate: model.editConfiguration?.allowQuickCreate,
        allowDuplicateEntry: model.editConfiguration?.allowDuplicateEntry,
        showWorkflowAction: model.editConfiguration?.showWorkflowAction,
        showSidebar: model.editConfiguration?.showSidebar,
        supportedOperations: model.editConfiguration?.supportedOperations?.map(
          (opt) => ({
            afterSave: opt.afterSave,
            operationType: opt.operation,
            fields: opt.fields,
            allowSaveInvalidEntries: opt.allowSaveInvalidEntries,

            requiredFields: opt.requiredFields,
            ...(model.editConfiguration?.allowInlineEdit &&
              opt.operation === EntryActionOperationEnum.AllowEditEntries && {
                allowInlineEdit: true,
              }),
            ...(model.editConfiguration?.allowCreateEntryAsDraft &&
              opt.operation ===
                EntryActionOperationEnum.AllowCreateNewEntries && {
                allowCreateEntryAsDraft: true,
              }),
            ...(model.editConfiguration?.allowQuickCreate &&
              opt.operation ===
                EntryActionOperationEnum.AllowCreateNewEntries && {
                allowQuickCreate: true,
              }),
            ...(model.editConfiguration?.allowDuplicateEntry &&
              opt.operation ===
                EntryActionOperationEnum.AllowCreateNewEntries && {
                allowDuplicateEntry: true,
              }),
          }),
        ),
      },
      fields: model.fields,
      icon: model.icon,
      // Rahaf todo remove this
      id: model.id ?? model['_id'],
      isPinned: model.isPinned,
      isPublished: model.isPublished,
      isImplicit: model.isImplicit,
      name: model.name,
      viewConfiguration: {
        ...model.viewConfiguration,
        defaultSorting: model.viewConfiguration.defaultSorting?.map((sort) => ({
          sortBy: sort.field,
          sortDir: sort.ascending ? 'asc' : 'desc',
        })),
      },
      entriesConfiguration: {
        ...model.entriesConfiguration,
        history: {
          ...model.entriesConfiguration.history,
          showTriggerHistory: model.entriesConfiguration.showTriggerHistory,
        },
        allowedTemplates: model.entriesConfiguration.allowedTemplates,
        showWorkflowHistory: this.normalizeWorkflowHistory(
          model.entriesConfiguration.showWorkflowHistory,
        ),
        supportPaginationBetweenEntries: true,
      },
      anonymous: model.anonymous,
      authenticated: model.authenticated,
      dashboardConfiguration: model.dashboardConfiguration,
      sideBarConfig: model?.sideBarConfig ?? {
        openMode: 0,
      },
      relations: model?.relations,
      module: model.module,
      displayOrder: model.displayOrder,
      systemButtons: model.systemButtons ?? [],
      systemButtonsOrder: model.systemButtonsOrder ?? [],
      uiAttributes: model.uiAttributes as unknown as DataViewStyle,
      sharing: {
        ...model.sharing,
        level: model.sharing.level.id,
      },
      updateIdentifier: model.updateIdentifier,
      redirectionRules: model.redirection
        ? JSON.stringify(model.redirection)
        : undefined,
      qrCode: model.qrCode,
      help: model.help,
      defaultViewTemplate: model.defaultViewTemplate,
      displayConfig: model?.displayConfig
        ? this._mapDisplayConfigToPresenterModel(model.displayConfig)
        : this._getDefaultDisplayOptions(),
    } as DataViewsPresenterModel;

    return presenter;
  }

  getDataModel(presenter: DataViewsPresenterModel): EndUserViewDto {
    const model = {
      name: presenter.name,
      dataEntity: presenter.dataEntity,
      dataSource: presenter.dataSource,
      description: presenter.description,
      fields: presenter.fields,
      icon: presenter.icon,
      isPinned: presenter.isPinned,
      isPublished: presenter.isPublished,
      domain: presenter.domain ?? undefined,
      specificationVersion: this.version,
      implicitType: presenter.implicitType,
      editConfiguration: {
        enabled: presenter.editConfiguration?.enabled,
        adminActions: presenter?.editConfiguration.adminActions,
        allowInlineEdit:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowEditEntries,
          )?.allowInlineEdit || undefined,
        allowCreateEntryAsDraft:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowCreateNewEntries,
          )?.allowCreateEntryAsDraft || undefined,
        allowQuickCreate:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowCreateNewEntries,
          )?.allowQuickCreate || undefined,
        allowDuplicateEntry:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowCreateNewEntries,
          )?.allowDuplicateEntry || undefined,
        showWorkflowAction: presenter?.editConfiguration?.showWorkflowAction,
        showSidebar: presenter?.editConfiguration?.showSidebar,
        supportedOperations:
          presenter.editConfiguration?.supportedOperations?.map((opt) => ({
            afterSave: opt.afterSave,
            operation: opt.operationType,
            fields: opt.fields,
            requiredFields: opt.requiredFields,
            allowSaveInvalidEntries: opt.allowSaveInvalidEntries,
          })),
      },
      viewConfiguration: {
        ...presenter.viewConfiguration,
        defaultSorting: presenter.viewConfiguration.defaultSorting?.map(
          (sort) => ({
            field: sort.sortBy,
            ascending: sort.sortDir === 'asc',
          }),
        ),
      },
      entriesConfiguration: {
        enabled: presenter.entriesConfiguration.enabled,
        fields: presenter.entriesConfiguration.fields,
        discussions: presenter.entriesConfiguration.discussions,
        history: {
          enabled: presenter.entriesConfiguration.history?.enabled ?? false,
          showChanger:
            presenter.entriesConfiguration.history?.showChanger ?? false,
          showChanges:
            presenter.entriesConfiguration.history?.showChanges ?? false,
          showReactionHistory:
            presenter.entriesConfiguration.history?.showTriggerHistory
              ?.enabled ?? false,
        },
        showTriggerHistory: {
          enabled:
            presenter.entriesConfiguration.history?.showTriggerHistory
              ?.enabled ?? false,
          showTriggerDetails:
            presenter.entriesConfiguration.history?.showTriggerHistory
              ?.showTriggerDetails ?? false,
        },
        customTemplate: presenter.entriesConfiguration.customTemplate,
        supportPaginationBetweenEntries: true,
        // presenter.entriesConfiguration.supportPaginationBetweenEntries,
        allowedTemplates:
          presenter.entriesConfiguration?.allowedTemplates ?? [],
        showWorkflowHistory: this.normalizeWorkflowHistory(
          presenter.entriesConfiguration.showWorkflowHistory,
        ),
        showEmailConfig: presenter.entriesConfiguration?.showEmailConfig ?? [],
        defaultSidebar:
          presenter.entriesConfiguration?.defaultSidebar ??
          DefaultSidebarEnum.None,
        showInformation: presenter.entriesConfiguration?.showInformation,
      },
      permissions: presenter.permissions,

      anonymous: {
        defaultView: presenter.anonymous.defaultView,
        enabled: presenter.anonymous.enabled,
        urls: presenter.anonymous.urls ?? [],
      },
      authenticated: {
        defaultView: presenter.authenticated.defaultView,
        enabled: presenter.authenticated.enabled,
        isRestricted: presenter.authenticated.isRestricted,
      },
      dashboardConfiguration: presenter.dashboardConfiguration,
      sideBarConfig: presenter.sideBarConfig,
      relations: presenter.relations,
      displayOrder:
        presenter.displayOrder !== null ? presenter.displayOrder : undefined,
      systemButtons: presenter.systemButtons ?? [],
      systemButtonsOrder: presenter.systemButtonsOrder ?? [],
      uiAttributes: presenter.uiAttributes,
      sharing: {
        ...presenter.sharing,
        level: {
          id: presenter.sharing.level,
        },
      },
      updateIdentifier: presenter.updateIdentifier,
      redirectionRules: presenter?.redirectionRules
        ? JSON.parse(presenter.redirectionRules)
        : undefined,
      qrCode: presenter.qrCode,
      defaultViewTemplate: presenter.defaultViewTemplate,
      displayConfig: this._mapDisplayConfigToDataModel(presenter.displayConfig),
    } as EndUserViewDto;

    return model;
  }

  private normalizeWorkflowHistory(
    config?: ShowWorkflowConfig | null,
  ): ShowWorkflowConfig | undefined {
    if (!config) {
      return config ?? undefined;
    }

    return {
      ...config,
      enableHistoryPath: config.enableHistoryPath ?? true,
    };
  }

  private _mapDisplayConfigToPresenterModel(
    config: GenericViewDisplayConfig,
  ): ViewDisplayOptions {
    const { pageContainerLayout, ...model } = config;

    return {
      ...model,
      pageContainedWidth:
        pageContainerLayout?.mode === PageContainerLayoutMode.Contained,
    };
  }
  private _mapDisplayConfigToDataModel(
    config: ViewDisplayOptions,
  ): GenericViewDisplayConfig {
    const { pageContainedWidth, ...model } = config;

    return {
      ...model,
      pageContainerLayout: {
        mode: pageContainedWidth
          ? PageContainerLayoutMode.Contained
          : PageContainerLayoutMode.Fluid,
      } satisfies PageContainerLayoutType,
    };
  }

  private _getDefaultDisplayOptions(): ViewDisplayOptions {
    return {
      transparent: false,
      fullPage: false,
      outerSpacing: true,
      showPageHeader: true,
      pageContainedWidth: false,
    };
  }
}
